import { createAsyncThunk } from '@reduxjs/toolkit'
import { API } from '@shared/api'
import { TypeMappingRestOutput } from '@shared/api/middleware/mappingAPI/restaurant/type'
import { globalStore } from '@app/model/store'
import { setMapData, setRestaurantsIdsBboxStatus, setRestaurantsStatus } from '@widgets/Restaurant/Map/model/slice'
import { TypeMapContextState } from '@widgets/Restaurant/Map/MapContext'
import { EGG } from '@shared/api/analytics'
import { ControllerRequestSignalsMap } from '@widgets/Restaurant/Map/helpers/controllerRequestSignals'

const pageSize = (zoom) => {
  if (zoom < 9) return 5
  else if (zoom < 10) return 10
  else if (zoom < 11) return 15
  else return 25
}

const maxPage = (zoom) => {
  if (zoom >= 15.5) return 100
  else if (zoom >= 15) return 10
  else if (zoom >= 14) return 5
  else if (zoom >= 12) return 3
  else if (zoom >= 10) return 2
  else return 1
}

let timeout = null

type TypePropsFetchRests = {
  page?: number
  clearRequest?: boolean // Новый чистый запрос. Останавливаются все предыдущие запросы. Используется при обновлении выбранных фильтров
}
type TypeResultFetchRests = {
  data: {
    nearestCityId: number
    needToCreateRests: TypeMappingRestOutput[]
    needToDeleteRestsIdx: number[]
    restaurantsIdsBbox: number[]
  }
  requestsLength: number
}

export const fetchRests = createAsyncThunk(
  'map/fetchRests',
  async (props: TypePropsFetchRests, { dispatch }): Promise<TypeResultFetchRests> => {
    const { page, clearRequest } = Object.assign({ page: 1, clearRequest: false }, props)

    const storeMap = globalStore.getState().features.map
    const stateFilters = globalStore.getState().features.restaurant_filters
    const { center, bbox, zoom } = storeMap.map
    const centerBeforeResponse = [...center]
    const timeBeforeRequest = Date.now()
    if (page == 1) clearTimeout(timeout)
    if (clearRequest) ControllerRequestSignalsMap.clearSignals()

    dispatch(setRestaurantsStatus('loading'))

    const fetchSignal = ControllerRequestSignalsMap.createSignal()

    const response = await API.restaurant_list({
      params: {
        ...stateFilters.output.paramsRequest,
        bbox: `${bbox.southWest.join(',')},${bbox.northEast.join(',')}`,
        page: page,
        page_size: pageSize(zoom),
        city: undefined,
      },
      config: { signal: fetchSignal },
    })
      .then((res) => {
        // Логика отправки запросов для получения след. пачки данных
        // - истек минимальный временной интервал между запросами (debounce)
        // - за пределами области просмотра есть рестораны, которые можно будет если что удалить (new Worker)
        // - центр карты до запроса совпадает с центром после получения данных

        const storeMap = globalStore.getState().features.map
        if (!storeMap.active) return

        const nextPageNumber = res.pagination.page.next.number
        const centersSame = centerBeforeResponse.every((coords, idx) => coords === storeMap.map.center[idx])

        if (centersSame && nextPageNumber && nextPageNumber <= maxPage(zoom)) {
          const worker = new Worker(new URL('../workers/webWorker.ts', import.meta.url), { type: 'module' })
          worker.postMessage({
            type: 'check-need-load-page',
            restaurants: storeMap.restaurants.data,
            bbox: storeMap.map.bbox,
            maxRests: storeMap.maxRests,
          })

          worker.onmessage = (e) => {
            if (e.data) dispatch(fetchRests({ page: nextPageNumber }))
            worker.terminate()
          }
        }
        return res
      })
      .finally(() => ControllerRequestSignalsMap.abortSignal(fetchSignal))

    return new Promise((resolve) => {
      const store = globalStore.getState()
      const storeMap = store.features.map
      const worker = new Worker(new URL('../workers/webWorker.ts', import.meta.url), { type: 'module' })
      worker.postMessage({
        type: 'handle-response-rests',
        restaurants: {
          old: storeMap.restaurants.data,
          new: response.results,
          oldIdsBbox: storeMap.restaurantsIdsBbox.data,
        },

        mapCenter: center,
        mapBbox: storeMap.map.bbox,
        maxRests: storeMap.maxRests,
      })

      worker.onmessage = (e) => {
        const requestsLength = ControllerRequestSignalsMap.getSignals().length
        const timeAfterRequest = Date.now()
        const timeDiff = timeAfterRequest - timeBeforeRequest

        resolve({ data: e.data, requestsLength })

        if (page == 1) {
          clearTimeout(timeout)
          timeout = setTimeout(
            () => {
              dispatch(setRestaurantsIdsBboxStatus(e.data.restaurantsIdsBbox.length ? 'success' : 'empty'))
            },
            timeDiff > 2000 ? 0 : 2000 - timeDiff,
          )
        }

        worker.terminate()
      }
    })
  },
)

type TypeSetMapGeolocation = {
  mapInstance: TypeMapContextState
  eventType: 'init' | 'click'
}
export const setMapGeolocation = createAsyncThunk(
  'map/setMapGeolocation',
  async (data: TypeSetMapGeolocation, { dispatch, getState }): Promise<[number, number]> =>
    new Promise((resolve, reject) => {
      if (data.eventType === 'init') {
        if (!navigator.permissions) resolve(null)

        navigator.permissions
          .query({ name: 'geolocation' })
          .then((permission) =>
            permission.state === 'granted'
              ? navigator.geolocation.getCurrentPosition((position) =>
                  resolve([position.coords.longitude, position.coords.latitude]),
                )
              : resolve(null),
          )
      }

      if (data.eventType === 'click') {
        EGG.pages.Map.click_geolocation()
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const coords = [position.coords.longitude, position.coords.latitude]
            dispatch(setMapData({ center: coords, zoom: 13 }))
            resolve([position.coords.longitude, position.coords.latitude])
          },
          () => {
            resolve(null)
          },
        )
      }
    }),
)

export const fetchRestsBySlugs = createAsyncThunk(
  'map/fetchRestsBySlugs',
  async (action: string[], { dispatch, getState }) => {
    return Promise.all(action.map((slug) => API.restaurant_read({ path: slug })))
  },
)

export const fetchRestGallery = createAsyncThunk('map/fetchRestGallery', async (id: number, { dispatch, getState }) => {
  return API.photo_list({ params: { page_size: 50, restaurant: Number(id) } }).then((res) => ({ ...res, restId: id }))
})
